import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/currency';
import { getApiUrl } from '../config';
import './Upcoming.css';

const Upcoming = ({ config }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showWizard, setShowWizard] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null); // null = adding new, number = editing existing
  const [accounts, setAccounts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('due_date');
  const [newTransaction, setNewTransaction] = useState({
    payee: '',
    due_date: '',
    amount: '',
    type: 'BILL',
    credit_account: '',
    debit_account: '',
    frequency: 'monthly',
    ask_amount: false
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editingTransactionIndex, setEditingTransactionIndex] = useState(null);
  const [transactionToAdd, setTransactionToAdd] = useState({
    date: '',
    payee: '',
    note: '',
    frequency: '',
    type: '',
    postings: []
  });

  useEffect(() => {
    loadTransactions();
    loadAccounts();
  }, [config]);

  const loadTransactions = async () => {
    if (!config.transactionsPath) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(getApiUrl('/api/transactions/read'), {
        filePath: config.transactionsPath
      });

      const txs = response.data.transactions.map(t => ({
        ...t,
        dueDate: new Date(t.due_date)
      }));

      // Sort by due date
      txs.sort((a, b) => a.dueDate - b.dueDate);

      setTransactions(txs);
      setLoading(false);
    } catch (err) {
      // File doesn't exist yet - that's okay
      if (err.response?.status === 404) {
        setShowWizard(true);
      } else {
        setError('Error loading transactions: ' + (err.response?.data?.error || err.message));
      }
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const response = await axios.post(getApiUrl('/api/hledger/accounts'), {
        journalPath: config.journalPath
      });
      setAccounts(response.data.accounts);
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  };

  const saveTransactions = async (txs) => {
    try {
      await axios.post(getApiUrl('/api/transactions/write'), {
        filePath: config.transactionsPath,
        transactions: txs
      });
    } catch (err) {
      throw new Error('Failed to save transactions: ' + err.message);
    }
  };

  const handleAddTransaction = async (e) => {
    e.preventDefault();

    try {
      let txsToSave;

      if (editingIndex !== null) {
        // Editing existing transaction
        txsToSave = transactions.map((t, idx) => {
          if (idx === editingIndex) {
            return {
              ...newTransaction,
              amount: newTransaction.ask_amount ? null : (newTransaction.amount ? parseFloat(newTransaction.amount) : null)
            };
          }
          return {
            ...t,
            due_date: t.dueDate.toISOString().split('T')[0]
          };
        });
      } else {
        // Adding new transaction
        txsToSave = [
          ...transactions.map(t => ({
            ...t,
            due_date: t.dueDate.toISOString().split('T')[0]
          })),
          {
            ...newTransaction,
            amount: newTransaction.ask_amount ? null : (newTransaction.amount ? parseFloat(newTransaction.amount) : null)
          }
        ];
      }

      await saveTransactions(txsToSave);
      await loadTransactions();
      setShowWizard(false);
      setEditingIndex(null);
      setNewTransaction({
        payee: '',
        due_date: '',
        amount: '',
        type: 'BILL',
        credit_account: '',
        debit_account: '',
        frequency: 'monthly',
        ask_amount: false
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditTransaction = (tx, index) => {
    // Populate modal with transaction data for editing
    let postings = [];

    // Check if transaction already has postings array (new format)
    if (tx.postings && Array.isArray(tx.postings) && tx.postings.length > 0) {
      // Use existing postings from JSON
      postings = tx.postings.map(p => {
        if (Array.isArray(p)) {
          // Array format: ["Expenses:Mortgage:Principal", "707.93"]
          return {
            account: p[0] || '',
            amount: p[1] !== undefined && p[1] !== null && p[1] !== '' ? p[1].toString() : ''
          };
        } else {
          // Object format: {account, amount}
          return {
            account: p.account || '',
            amount: p.amount !== undefined && p.amount !== null ? p.amount.toString() : ''
          };
        }
      });
    } else {
      // Convert old simple format (debit_account/credit_account) to postings
      if (tx.debit_account) {
        postings.push({
          account: tx.debit_account,
          amount: tx.ask_amount ? '' : (tx.amount ? tx.amount.toString() : '')
        });
      }
      if (tx.credit_account) {
        postings.push({
          account: tx.credit_account,
          amount: tx.ask_amount ? '' : (tx.amount ? (-tx.amount).toString() : '')
        });
      }
    }

    // Ensure we have at least 2 postings
    if (postings.length < 2) {
      postings = [
        { account: '', amount: '' },
        { account: '', amount: '' }
      ];
    }

    setTransactionToAdd({
      date: tx.due_date,
      payee: tx.payee,
      note: '',
      frequency: tx.frequency,
      type: tx.type || 'BILL',
      postings: postings
    });
    setEditingTransaction(tx);
    setEditingTransactionIndex(index);
    setModalMode('edit');
    setShowEditModal(true);
  };

  const handleDeleteTransaction = async (index) => {
    if (!window.confirm('Are you sure you want to delete this recurring transaction?')) {
      return;
    }

    try {
      const txsToSave = transactions
        .filter((_, idx) => idx !== index)
        .map(t => ({
          ...t,
          due_date: t.dueDate.toISOString().split('T')[0]
        }));

      await saveTransactions(txsToSave);
      await loadTransactions();
    } catch (err) {
      setError('Failed to delete transaction: ' + err.message);
    }
  };

  const handleSkipTransaction = async (tx, index) => {
    if (!window.confirm(`Skip this occurrence and move to next ${tx.frequency} due date?`)) {
      return;
    }

    try {
      const nextDueDate = calculateNextDueDate(tx.dueDate, tx.frequency);

      const txsToSave = transactions.map((t, idx) => {
        if (idx === index) {
          return {
            ...t,
            due_date: nextDueDate.toISOString().split('T')[0]
          };
        }
        return {
          ...t,
          due_date: t.dueDate.toISOString().split('T')[0]
        };
      });

      await saveTransactions(txsToSave);
      await loadTransactions();
    } catch (err) {
      setError('Failed to skip transaction: ' + err.message);
    }
  };

  const handleAddToJournal = (tx) => {
    // Open modal with prefilled transaction data for adding to journal
    const today = new Date().toISOString().split('T')[0];

    let postings = [];

    // Check if transaction already has postings array (new format)
    if (tx.postings && Array.isArray(tx.postings) && tx.postings.length > 0) {
      // Use existing postings from JSON
      // Postings can be either arrays [account, amount] or objects {account, amount}
      postings = tx.postings.map(p => {
        if (Array.isArray(p)) {
          // Array format: ["Expenses:Mortgage:Principal", "707.93"]
          return {
            account: p[0] || '',
            amount: p[1] !== undefined && p[1] !== null && p[1] !== '' ? p[1].toString() : ''
          };
        } else {
          // Object format: {account: "...", amount: ...}
          return {
            account: p.account || '',
            amount: p.amount !== undefined && p.amount !== null ? p.amount.toString() : ''
          };
        }
      });
    } else {
      // Convert old simple format (debit_account/credit_account) to postings
      if (tx.debit_account) {
        postings.push({
          account: tx.debit_account,
          amount: tx.ask_amount ? '' : (tx.amount ? tx.amount.toString() : '')
        });
      }
      if (tx.credit_account) {
        postings.push({
          account: tx.credit_account,
          amount: tx.ask_amount ? '' : (tx.amount ? (-tx.amount).toString() : '')
        });
      }
    }

    // Ensure we have at least 2 postings
    if (postings.length < 2) {
      postings = [
        { account: '', amount: '' },
        { account: '', amount: '' }
      ];
    }

    setTransactionToAdd({
      date: tx.due_date || today,
      payee: tx.payee,
      note: '',
      frequency: '',
      type: '',
      postings: postings
    });
    setEditingTransaction(tx);
    setModalMode('add');
    setShowEditModal(true);
  };

  const handleConfirmAddToJournal = async () => {
    if (modalMode === 'edit') {
      return handleSaveEditedRecurring();
    }

    try {
      const { date, payee, note, postings } = transactionToAdd;

      if (!date || !payee) {
        alert('Please fill in date and payee');
        return;
      }

      // Validate postings
      if (!postings || postings.length < 2) {
        alert('Transaction must have at least 2 postings');
        return;
      }

      // Check that all postings have accounts
      const invalidPostings = postings.filter(p => !p.account || !p.account.trim());
      if (invalidPostings.length > 0) {
        alert('All postings must have an account');
        return;
      }

      // Check that at least all but one posting has an amount (hledger can infer one)
      const postingsWithAmount = postings.filter(p => p.amount && p.amount.trim());
      if (postingsWithAmount.length < postings.length - 1) {
        alert('At least all but one posting must have an amount');
        return;
      }

      // Format transaction entry
      const noteText = note ? ` | ${note}` : '';
      let entry = `${date} ${payee}${noteText}\n`;

      postings.forEach(posting => {
        const amountText = posting.amount && posting.amount.trim() ? posting.amount : '';
        entry += `    ${posting.account.padEnd(50)} ${amountText}\n`;
      });

      await axios.post(getApiUrl('/api/journal/append'), {
        journalPath: config.journalPath,
        transaction: entry
      });

      // Update due date
      const updatedTx = {
        ...editingTransaction,
        dueDate: calculateNextDueDate(editingTransaction.dueDate, editingTransaction.frequency),
        due_date: calculateNextDueDate(editingTransaction.dueDate, editingTransaction.frequency).toISOString().split('T')[0]
      };

      const updatedTransactions = transactions.map(t =>
        t.payee === editingTransaction.payee && t.due_date === editingTransaction.due_date ? updatedTx : t
      );

      await saveTransactions(updatedTransactions.map(t => ({
        ...t,
        due_date: typeof t.due_date === 'string' ? t.due_date : t.dueDate.toISOString().split('T')[0]
      })));

      await loadTransactions();
      setShowEditModal(false);
      setEditingTransaction(null);
      alert('Transaction added to journal!');
    } catch (err) {
      alert('Error adding transaction: ' + err.message);
    }
  };

  const handleSaveEditedRecurring = async () => {
    try {
      const { date, payee, frequency, type, postings } = transactionToAdd;

      if (!date || !payee || !frequency || !type) {
        alert('Please fill in date, payee, frequency, and type');
        return;
      }

      // Validate postings
      if (!postings || postings.length < 2) {
        alert('Transaction must have at least 2 postings');
        return;
      }

      // Check that all postings have accounts
      const invalidPostings = postings.filter(p => !p.account || !p.account.trim());
      if (invalidPostings.length > 0) {
        alert('All postings must have an account');
        return;
      }

      // Convert postings to array format for storage
      const postingsToSave = postings.map(p => [p.account, p.amount]);

      // Update the transaction in the array
      const updatedTransactions = transactions.map((t, idx) => {
        if (idx === editingTransactionIndex) {
          return {
            payee: payee,
            due_date: date,
            frequency: frequency,
            type: type,
            postings: postingsToSave
          };
        }
        return {
          ...t,
          due_date: typeof t.due_date === 'string' ? t.due_date : t.dueDate.toISOString().split('T')[0]
        };
      });

      await saveTransactions(updatedTransactions);
      await loadTransactions();
      setShowEditModal(false);
      setEditingTransaction(null);
      setEditingTransactionIndex(null);
      alert('Recurring transaction updated!');
    } catch (err) {
      alert('Error updating transaction: ' + err.message);
    }
  };

  const calculateNextDueDate = (currentDate, frequency) => {
    const date = new Date(currentDate);
    const originalDay = date.getDate();
    const originalMonth = date.getMonth();

    switch (frequency) {
      case 'daily':
        date.setDate(date.getDate() + 1);
        break;
      case 'weekly':
        date.setDate(date.getDate() + 7);
        break;
      case 'monthly':
        date.setMonth(date.getMonth() + 1);
        // Handle month-end dates (e.g., Jan 31 -> Feb 28/29)
        if (date.getDate() !== originalDay) {
          date.setDate(0); // Set to last day of previous month
        }
        break;
      case 'quarterly':
        date.setMonth(date.getMonth() + 3);
        if (date.getDate() !== originalDay) {
          date.setDate(0);
        }
        break;
      case 'semi-annually':
        date.setMonth(date.getMonth() + 6);
        if (date.getDate() !== originalDay) {
          date.setDate(0);
        }
        break;
      case 'yearly':
        date.setFullYear(date.getFullYear() + 1);
        // Handle leap year edge case (Feb 29 -> Mar 1 in non-leap years)
        if (date.getMonth() !== originalMonth) {
          date.setDate(0); // Set to Feb 28 instead of Mar 1
        }
        break;
      default:
        // Assume it's a number of days
        if (!isNaN(frequency)) {
          date.setDate(date.getDate() + parseInt(frequency));
        }
    }

    return date;
  };

  const getDaysUntilDue = (dueDate) => {
    const today = new Date();
    const days = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getUrgencyClass = (days) => {
    if (days < 0) return 'overdue';
    if (days <= 3) return 'urgent';
    if (days <= 7) return 'soon';
    return 'normal';
  };

  const getFilteredAndSortedTransactions = () => {
    let filtered = transactions;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(tx => {
        const payee = (tx.payee || '').toLowerCase();
        const creditAccount = (tx.credit_account || '').toLowerCase();
        const debitAccount = (tx.debit_account || '').toLowerCase();

        // Also search in postings if present
        let postingsMatch = false;
        if (tx.postings && Array.isArray(tx.postings)) {
          postingsMatch = tx.postings.some(p => {
            const account = Array.isArray(p) ? (p[0] || '').toLowerCase() : (p.account || '').toLowerCase();
            return account.includes(query);
          });
        }

        return payee.includes(query) || creditAccount.includes(query) || debitAccount.includes(query) || postingsMatch;
      });
    }

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(tx => tx.type === filterType);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'due_date':
          return a.dueDate - b.dueDate;
        case 'due_date_desc':
          return b.dueDate - a.dueDate;
        case 'payee':
          return (a.payee || '').localeCompare(b.payee || '');
        case 'amount':
          const amtA = a.amount || 0;
          const amtB = b.amount || 0;
          return amtB - amtA; // Descending
        default:
          return 0;
      }
    });

    return filtered;
  };

  if (!config.transactionsPath) {
    return (
      <div>
        <div className="page-header">
          <h1>Upcoming Transactions</h1>
        </div>
        <div className="alert alert-warning">
          No transactions file configured. Please set it up in Settings.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Loading upcoming transactions...</p>
      </div>
    );
  }

  return (
    <div className="upcoming-page">
      <div className="page-header">
        <h1>Upcoming Transactions</h1>
        <p>Manage recurring bills and income</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Recurring Transactions</h2>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              if (showWizard) {
                setShowWizard(false);
                setEditingIndex(null);
              } else {
                setShowWizard(true);
                setEditingIndex(null);
                setNewTransaction({
                  payee: '',
                  due_date: '',
                  amount: '',
                  type: 'BILL',
                  credit_account: '',
                  debit_account: '',
                  frequency: 'monthly',
                  ask_amount: false
                });
              }
            }}
          >
            {showWizard ? 'Cancel' : '+ Add Transaction'}
          </button>
        </div>
        <div className="card-body">
          {showWizard && (
            <form onSubmit={handleAddTransaction} className="transaction-wizard">
              <div className="wizard-grid">
                <div className="form-group">
                  <label className="form-label">Payee *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newTransaction.payee}
                    onChange={(e) => setNewTransaction({ ...newTransaction, payee: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Type *</label>
                  <select
                    className="form-select"
                    value={newTransaction.type}
                    onChange={(e) => setNewTransaction({ ...newTransaction, type: e.target.value })}
                  >
                    <option value="BILL">Bill</option>
                    <option value="INCOME">Income</option>
                    <option value="TRANSFER">Transfer</option>
                    <option value="INVEST">Investment</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Due Date *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={newTransaction.due_date}
                    onChange={(e) => setNewTransaction({ ...newTransaction, due_date: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Frequency *</label>
                  <select
                    className="form-select"
                    value={newTransaction.frequency}
                    onChange={(e) => setNewTransaction({ ...newTransaction, frequency: e.target.value })}
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="semi-annually">Semi-annually</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={newTransaction.amount}
                    onChange={(e) => setNewTransaction({ ...newTransaction, amount: e.target.value })}
                    disabled={newTransaction.ask_amount}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={newTransaction.ask_amount}
                      onChange={(e) => setNewTransaction({ ...newTransaction, ask_amount: e.target.checked })}
                    />
                    <span style={{ fontSize: '13px' }}>Ask each time</span>
                  </label>
                </div>

                <div className="form-group">
                  <label className="form-label">Debit Account *</label>
                  <input
                    type="text"
                    className="form-input"
                    list="accounts-list"
                    value={newTransaction.debit_account}
                    onChange={(e) => setNewTransaction({ ...newTransaction, debit_account: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Credit Account *</label>
                  <input
                    type="text"
                    className="form-input"
                    list="accounts-list"
                    value={newTransaction.credit_account}
                    onChange={(e) => setNewTransaction({ ...newTransaction, credit_account: e.target.value })}
                    required
                  />
                </div>
              </div>

              <datalist id="accounts-list">
                {accounts.map((account, index) => (
                  <option key={index} value={account} />
                ))}
              </datalist>

              <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
                <button type="submit" className="btn btn-primary">
                  {editingIndex !== null ? 'Update Transaction' : 'Add Transaction'}
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setShowWizard(false);
                    setEditingIndex(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {!showWizard && transactions.length === 0 && (
            <div className="empty-state">
              <p>No recurring transactions found</p>
              <button className="btn btn-primary" onClick={() => setShowWizard(true)}>
                Add Your First Transaction
              </button>
            </div>
          )}

          {!showWizard && transactions.length > 0 && (
            <>
              {/* Search and Filter Controls */}
              <div style={{ marginBottom: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: '12px' }}>Search</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Search payee or account..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: '12px' }}>Filter by Type</label>
                  <select
                    className="form-select"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <option value="all">All Types</option>
                    <option value="BILL">Bills</option>
                    <option value="INCOME">Income</option>
                    <option value="TRANSFER">Transfers</option>
                    <option value="INVEST">Investments</option>
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: '12px' }}>Sort by</label>
                  <select
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="due_date">Due Date (Soonest First)</option>
                    <option value="due_date_desc">Due Date (Latest First)</option>
                    <option value="payee">Payee (A-Z)</option>
                    <option value="amount">Amount (High to Low)</option>
                  </select>
                </div>
              </div>

              <div className="upcoming-list">
              {getFilteredAndSortedTransactions().map((tx) => {
                // Find the original index in the unfiltered array
                const originalIndex = transactions.findIndex(t => t.payee === tx.payee && t.due_date === tx.due_date);
                const daysUntil = getDaysUntilDue(tx.dueDate);
                const urgency = getUrgencyClass(daysUntil);

                return (
                  <div key={`${tx.payee}-${tx.due_date}`} className={`upcoming-card ${urgency}`}>
                    <div className="upcoming-card-header">
                      <div style={{ flex: 1 }}>
                        <h3 className="upcoming-card-title">{tx.payee}</h3>
                        <p className="upcoming-card-meta">
                          {tx.type} • {tx.frequency}
                        </p>
                      </div>
                      <div className="upcoming-card-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {/* Icon-only action buttons */}
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          <button
                            className="btn"
                            onClick={() => handleEditTransaction(tx, originalIndex)}
                            title="Edit"
                            style={{ padding: '0.25rem 0.5rem', fontSize: '14px', minWidth: 'auto' }}
                          >
                            ✎
                          </button>
                          <button
                            className="btn"
                            onClick={() => handleSkipTransaction(tx, originalIndex)}
                            title="Skip to next occurrence"
                            style={{ padding: '0.25rem 0.5rem', fontSize: '14px', minWidth: 'auto' }}
                          >
                            ⏭
                          </button>
                          <button
                            className="btn"
                            onClick={() => handleDeleteTransaction(originalIndex)}
                            title="Delete"
                            style={{ padding: '0.25rem 0.5rem', fontSize: '14px', minWidth: 'auto', color: 'var(--danger)' }}
                          >
                            🗑
                          </button>
                        </div>
                        {/* Primary action button */}
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleAddToJournal(tx)}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          + Journal
                        </button>
                      </div>
                    </div>
                    <div className="upcoming-card-body">
                      <div className="upcoming-detail">
                        <span className="detail-label">Due Date:</span>
                        <span>{tx.dueDate.toLocaleDateString()}</span>
                        <span className={`badge badge-${urgency === 'overdue' ? 'danger' : urgency === 'urgent' ? 'warning' : 'info'}`}>
                          {daysUntil < 0 ? `${Math.abs(daysUntil)}d overdue` : `${daysUntil}d`}
                        </span>
                      </div>

                      {/* Show postings if available, otherwise show old format */}
                      {tx.postings && Array.isArray(tx.postings) && tx.postings.length > 0 ? (
                        <>
                          <div className="upcoming-detail">
                            <span className="detail-label">Postings:</span>
                            <span style={{ fontSize: '12px', color: '#8b949e' }}>
                              {tx.postings.length} accounts
                            </span>
                          </div>
                          {tx.postings.map((posting, pIdx) => {
                            // Handle both array format [account, amount] and object format {account, amount}
                            const account = Array.isArray(posting) ? posting[0] : posting.account;
                            const amount = Array.isArray(posting) ? posting[1] : posting.amount;

                            return (
                              <div key={pIdx} className="upcoming-detail" style={{ paddingLeft: '1rem', fontSize: '12px' }}>
                                <span className="text-muted">{account || ''}</span>
                                <span className="text-mono">
                                  {amount !== undefined && amount !== null && amount !== ''
                                    ? (typeof amount === 'number' ? formatCurrency(amount) : amount)
                                    : '(auto)'}
                                </span>
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <>
                          <div className="upcoming-detail">
                            <span className="detail-label">Amount:</span>
                            <span className="text-mono">
                              {tx.ask_amount ? 'TBD' : formatCurrency(tx.amount)}
                            </span>
                          </div>
                          {tx.credit_account && (
                            <div className="upcoming-detail">
                              <span className="detail-label">From:</span>
                              <span className="text-muted">{tx.credit_account}</span>
                            </div>
                          )}
                          {tx.debit_account && (
                            <div className="upcoming-detail">
                              <span className="detail-label">To:</span>
                              <span className="text-muted">{tx.debit_account}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </div>
      </div>

      {/* Edit Transaction Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalMode === 'edit' ? 'Edit Recurring Transaction' : 'Review Transaction'}</h2>
              <button
                className="modal-close"
                onClick={() => setShowEditModal(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem', color: '#8b949e', fontSize: '13px' }}>
                {modalMode === 'edit'
                  ? 'Edit the recurring transaction details'
                  : 'Review and edit the transaction details before adding to journal'}
              </p>
              <div className="form-group">
                <label className="form-label">{modalMode === 'edit' ? 'Next Due Date *' : 'Date *'}</label>
                <input
                  type="date"
                  className="form-input"
                  value={transactionToAdd.date}
                  onChange={(e) => setTransactionToAdd({ ...transactionToAdd, date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Payee *</label>
                <input
                  type="text"
                  className="form-input"
                  value={transactionToAdd.payee}
                  onChange={(e) => setTransactionToAdd({ ...transactionToAdd, payee: e.target.value })}
                />
              </div>

              {modalMode === 'edit' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Type *</label>
                    <select
                      className="form-select"
                      value={transactionToAdd.type}
                      onChange={(e) => setTransactionToAdd({ ...transactionToAdd, type: e.target.value })}
                    >
                      <option value="BILL">Bill</option>
                      <option value="INCOME">Income</option>
                      <option value="TRANSFER">Transfer</option>
                      <option value="INVEST">Investment</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Frequency *</label>
                    <select
                      className="form-select"
                      value={transactionToAdd.frequency}
                      onChange={(e) => setTransactionToAdd({ ...transactionToAdd, frequency: e.target.value })}
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="semi-annually">Semi-annually</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                </>
              )}

              {modalMode !== 'edit' && (
                <div className="form-group">
                  <label className="form-label">Note (Optional)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={transactionToAdd.note}
                    onChange={(e) => setTransactionToAdd({ ...transactionToAdd, note: e.target.value })}
                    placeholder="Additional note or description"
                  />
                </div>
              )}

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label className="form-label" style={{ margin: 0 }}>Postings *</label>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => {
                      setTransactionToAdd({
                        ...transactionToAdd,
                        postings: [...transactionToAdd.postings, { account: '', amount: '' }]
                      });
                    }}
                  >
                    + Add Posting
                  </button>
                </div>
                <p style={{ fontSize: '12px', color: '#8b949e', marginBottom: '0.75rem' }}>
                  Add multiple postings for split transactions (e.g., mortgage with principal, interest, escrow). Leave one amount blank to auto-calculate.
                </p>

                {transactionToAdd.postings.map((posting, index) => (
                  <div key={index} style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 150px 40px',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                    padding: '0.75rem',
                    background: 'var(--bg-primary)',
                    borderRadius: '4px',
                    border: '1px solid var(--border)'
                  }}>
                    <div>
                      <label style={{ fontSize: '11px', color: '#8b949e', display: 'block', marginBottom: '0.25rem' }}>
                        Account
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        list="accounts-list-modal"
                        value={posting.account}
                        onChange={(e) => {
                          const newPostings = [...transactionToAdd.postings];
                          newPostings[index].account = e.target.value;
                          setTransactionToAdd({ ...transactionToAdd, postings: newPostings });
                        }}
                        placeholder="e.g., Expenses:Car:Interest"
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: '#8b949e', display: 'block', marginBottom: '0.25rem' }}>
                        Amount
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={posting.amount}
                        onChange={(e) => {
                          const newPostings = [...transactionToAdd.postings];
                          newPostings[index].amount = e.target.value;
                          setTransactionToAdd({ ...transactionToAdd, postings: newPostings });
                        }}
                        placeholder="100 or -100"
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => {
                          const newPostings = transactionToAdd.postings.filter((_, i) => i !== index);
                          setTransactionToAdd({ ...transactionToAdd, postings: newPostings });
                        }}
                        disabled={transactionToAdd.postings.length <= 2}
                        title="Remove posting"
                        style={{ padding: '0.25rem 0.5rem' }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <datalist id="accounts-list-modal">
                {accounts.map((account, index) => (
                  <option key={index} value={account} />
                ))}
              </datalist>

              {/* Journal Preview - only show when adding to journal */}
              {modalMode !== 'edit' && (
                <div className="form-group" style={{ marginTop: '1.5rem' }}>
                  <label className="form-label">Preview - Journal Entry</label>
                  <p style={{ fontSize: '12px', color: '#8b949e', marginBottom: '0.5rem' }}>
                    This is what will be written to your journal file:
                  </p>
                  <pre style={{
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    padding: '1rem',
                    fontSize: '12px',
                    fontFamily: 'var(--font-mono)',
                    color: '#e6edf3',
                    overflow: 'auto',
                    whiteSpace: 'pre'
                  }}>
                    {(() => {
                      const { date, payee, note, postings } = transactionToAdd;
                      if (!date || !payee) return '(fill in date and payee to see preview)';

                      const noteText = note ? ` | ${note}` : '';
                      let preview = `${date} ${payee}${noteText}\n`;

                      postings.forEach(posting => {
                        const amountText = posting.amount && posting.amount.trim() ? posting.amount : '';
                        preview += `    ${posting.account.padEnd(50)} ${amountText}\n`;
                      });

                      return preview.trimEnd();
                    })()}
                  </pre>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleConfirmAddToJournal}>
                {modalMode === 'edit' ? 'Save Changes' : 'Add to Journal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Upcoming;
